import axios from 'axios';
import {
  SET_PODIO_LOADING,
  SET_PODIO_ERROR,
  FETCH_PODIO_ACCOUNTS_SUCCESS,
  FETCH_PODIO_ACCESS_TOKEN_SUCCESS,
  FETCH_PODIO_ORGS_SUCCESS,
  FETCH_PODIO_SPACES_SUCCESS,
  FETCH_PODIO_APPS_SUCCESS,
  SET_SELECTED_PODIO_ACCOUNT,
  SET_SELECTED_PODIO_ORG,
  SET_SELECTED_PODIO_SPACE,
  SET_SELECTED_PODIO_APP,
  CLEAR_PODIO_FROM_ORG,
  CLEAR_PODIO_FROM_SPACE,
  CLEAR_PODIO_FROM_ACCOUNT,
} from './type';

// Mergeasy backend base (for API 1 & 2)
const MERGEASY_BASE = process.env.REACT_APP_MERGEASY_BASE || 'https://mergeasy.com';

// Generic bearer from app token
const authHead = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
});

// Helper: check expiry
const isExpired = (expiresAtMs) => Date.now() >= expiresAtMs;

// ---------------- Selections & cascade clears ----------------

export const setSelectedPodioAccount = (accountId) => (dispatch) => {
  // when account changes, clear deeper data
  dispatch({ type: CLEAR_PODIO_FROM_ACCOUNT });
  dispatch({ type: SET_SELECTED_PODIO_ACCOUNT, payload: accountId });
};

export const setSelectedPodioOrg = (orgId) => (dispatch) => {
  // when org changes, clear spaces/apps
  dispatch({ type: CLEAR_PODIO_FROM_ORG });
  dispatch({ type: SET_SELECTED_PODIO_ORG, payload: orgId });
};

export const setSelectedPodioSpace = (spaceId) => (dispatch) => {
  // when space changes, clear apps
  dispatch({ type: CLEAR_PODIO_FROM_SPACE });
  dispatch({ type: SET_SELECTED_PODIO_SPACE, payload: spaceId });
};

export const setSelectedPodioApp = (appId) => ({ type: SET_SELECTED_PODIO_APP, payload: appId });

// ---------------- API 1: Accounts ----------------

export const fetchPodioAccounts = () => async (dispatch) => {
  dispatch({ type: SET_PODIO_LOADING });
  try {
    const res = await axios.get(`${MERGEASY_BASE}/api/podioAccounts`, authHead());
    // API returns { email, podioAccounts: [...] }
    dispatch({ type: FETCH_PODIO_ACCOUNTS_SUCCESS, payload: res.data?.podioAccounts || [] });
    return res.data?.podioAccounts || [];
  } catch (err) {
    dispatch({ type: SET_PODIO_ERROR, payload: err?.message || 'Failed to load Podio accounts' });
    return null;
  }
};

// ---------------- API 2: Access Token ----------------

export const getPodioAccessToken = (accountId) => async (dispatch, getState) => {
  const state = getState().podioLookup;
  const existing = state?.accessTokenByAccount?.[accountId];

  // reuse if not expired
  if (existing && !isExpired(existing.expiresAt)) {
    return existing.token;
  }

  dispatch({ type: SET_PODIO_LOADING });
  try {
    const res = await axios.post(
      `${MERGEASY_BASE}/api/podioAccessToken`,
      { podioAccountId: accountId },
      authHead()
    );
    const token = res.data?.access_token;
    const expiresIn = res.data?.expires_in || 0; // seconds
    const expiresAt = Date.now() + expiresIn * 1000 - 10_000; // minus 10s buffer

    dispatch({
      type: FETCH_PODIO_ACCESS_TOKEN_SUCCESS,
      payload: { accountId, token, expiresAt },
    });

    return token;
  } catch (err) {
    dispatch({ type: SET_PODIO_ERROR, payload: err?.message || 'Failed to get Podio token' });
    return null;
  }
};

// Helper: header with Podio OAuth token
const podioHead = (accessToken) => ({
  headers: { Authorization: `Bearer ${accessToken}` },
});

// ---------------- API 3: Orgs ----------------

export const fetchPodioOrgs = (accountId) => async (dispatch, getState) => {
  dispatch({ type: SET_PODIO_LOADING });
  try {
    const token = await dispatch(getPodioAccessToken(accountId));
    if (!token) throw new Error('Missing Podio access token');

    const res = await axios.get(`https://api.podio.com/org`, podioHead(token));
    const orgs = Array.isArray(res.data) ? res.data : [];

    dispatch({ type: FETCH_PODIO_ORGS_SUCCESS, payload: { accountId, orgs } });
    return orgs;
  } catch (err) {
    dispatch({ type: SET_PODIO_ERROR, payload: err?.message || 'Failed to fetch organizations' });
    return null;
  }
};

// ---------------- API 4: Spaces by Org ----------------

export const fetchPodioSpaces = (accountId, orgId) => async (dispatch, getState) => {
  dispatch({ type: SET_PODIO_LOADING });
  try {
    const token = await dispatch(getPodioAccessToken(accountId));
    if (!token) throw new Error('Missing Podio access token');

    const res = await axios.get(`https://api.podio.com/space/org/${orgId}`, podioHead(token));
    const spaces = Array.isArray(res.data) ? res.data : [];

    dispatch({ type: FETCH_PODIO_SPACES_SUCCESS, payload: { orgId, spaces } });
    return spaces;
  } catch (err) {
    dispatch({ type: SET_PODIO_ERROR, payload: err?.message || 'Failed to fetch workspaces' });
    return null;
  }
};

// ---------------- API 5: Apps by Space ----------------

export const fetchPodioApps = (accountId, spaceId) => async (dispatch) => {
  dispatch({ type: SET_PODIO_LOADING });
  try {
    const token = await dispatch(getPodioAccessToken(accountId));
    if (!token) throw new Error('Missing Podio access token');

    const res = await axios.get(`https://api.podio.com/app/space/${spaceId}`, podioHead(token));
    const apps = Array.isArray(res.data) ? res.data : [];

    dispatch({ type: FETCH_PODIO_APPS_SUCCESS, payload: { spaceId, apps } });
    return apps;
  } catch (err) {
    dispatch({ type: SET_PODIO_ERROR, payload: err?.message || 'Failed to fetch apps' });
    return null;
  }
};
